import * as React from 'react';

import { isEqual, omit } from 'lodash/fp';
import { DataTableQueryParams, OrderDirectionKey } from 'types';

import _ from '../locale';
import { WithQueryParamsInjectedProps } from '../utility/hoc/with-query-params';
import DataTable, { Props as DataTableProps, PARAM_KEYS } from './DataTable';

export interface Props
    extends WithQueryParamsInjectedProps<DataTableQueryParams>,
        Pick<
            DataTableProps,
            | 'renderActionButton'
            | 'renderHeaders'
            | 'renderItem'
            | 'renderFooter'
            | 'renderEmpty'
            | 'loadItems'
            | 'removeItem'
        > {
    defaultQueryParams: DataTableQueryParams;
    queryParams: DataTableQueryParams;
    setQueryParams: (params: any) => void;
    searchDebounce?: number;
    showSearchField?: boolean;
    hidePagination?: boolean;
    onItemsLoaded?: (state: any) => void;
}

type State = {};

class DataTableWithQueryParams extends React.Component<Props, State> {
    dataTableRef = React.createRef<DataTable>();

    // Proxying DataTable methods in order to have possibility to access
    // the DataTableWithQueryParams methods from refs
    loadItems = () => this.dataTableRef.current.loadItems();

    getTotalItemsCount = () => this.dataTableRef.current.getTotalItemsCount();

    static defaultProps = {};

    componentDidUpdate(prevProps, prevState) {
        const { queryParams } = this.props;
        const omitPaginationParams = omit(PARAM_KEYS);
        if (
            !isEqual(omitPaginationParams(queryParams), omitPaginationParams(prevProps.queryParams))
        ) {
            this.loadItems();
        }
    }

    getRenderProps = () => ({
        setSortOrder: this.setSortOrder,
        toggleSortDirection: this.toggleSortDirection,
    });

    getQueryParams = (): DataTableQueryParams => ({
        ...this.props.defaultQueryParams,
        ...this.props.queryParams,
    });

    handlePageChange = (page: number) => {
        this.props.setQueryParams({
            ...this.props.queryParams,
            page: page.toString(),
        });
    };

    handleSearchChange = (search: string) => {
        this.props.setQueryParams({
            ...this.props.queryParams,
            search,
            page: '1',
        });
    };

    setSortOrder = (order_by: string, order_direction: OrderDirectionKey = 'DESC') => {
        const { queryParams } = this.props;
        this.props.setQueryParams({
            ...queryParams,
            order_by,
            order_direction,
            page: '1',
        });
    };

    toggleSortDirection = () => {
        const { queryParams } = this.props;
        this.props.setQueryParams({
            ...queryParams,
            order_direction: queryParams.order_direction === 'DESC' ? 'ASC' : 'DESC',
        });
    };

    renderEmpty = () =>
        this.props.renderEmpty
            ? this.props.renderEmpty(this.getRenderProps())
            : this.props.queryParams.search
              ? _('data_table_error_no_search_results')
              : _('data_table_error_no_data');

    render() {
        const {
            loadItems,
            removeItem,
            renderActionButton,
            renderHeaders,
            renderItem,
            renderFooter,
            searchDebounce,
            showSearchField,
            hidePagination,
            onItemsLoaded,
        } = this.props;

        const { search, page, per_page, order_by, order_direction, ...restParams } =
            this.getQueryParams();

        return (
            <DataTable
                ref={this.dataTableRef}
                dataTableStyle={{ ...this.props.dataTableStyle }}
                loadItems={(params) => loadItems({ ...params, ...restParams })}
                removeItem={removeItem}
                renderActionButton={renderActionButton}
                renderHeaders={(baseRenderProps) =>
                    renderHeaders({
                        ...baseRenderProps,
                        ...this.getRenderProps(),
                    })
                }
                renderItem={renderItem}
                renderFooter={renderFooter}
                renderEmpty={this.renderEmpty}
                page={page ? parseInt(page) : undefined}
                perPage={per_page ? parseInt(per_page) : undefined}
                showSearchField={showSearchField}
                search={search}
                searchDebounce={searchDebounce}
                orderBy={order_by}
                orderDirection={order_direction}
                onPageChange={this.handlePageChange}
                onSearchChange={this.handleSearchChange}
                onItemsLoaded={onItemsLoaded}
                hidePagination={hidePagination}
            />
        );
    }
}

export default DataTableWithQueryParams;
